import { compoundInterest } from "./investment";
import { roundToDecimal } from "../utils/math";
import { getCountryRules } from "../country/registry";

export interface RetirementPlannerResult {
  corpusAccumulated: number;
  corpusNeeded: number;
  savingsShortfall: number;
  yearsToRetire: number;
  projection: Array<{
    age: number;
    savings: number;
    contributions: number;
    interest: number;
  }>;
}

/**
 * Retirement Planner Projections
 */
export function retirementPlanner(
  currentAge: number,
  retirementAge: number,
  lifeExpectancy: number,
  currentSavings: number,
  monthlySavings: number,
  annualReturnPre: number, // investment return pre-retirement (e.g. 8%)
  annualReturnPost: number, // investment return post-retirement (e.g. 5%)
  inflationRate: number, // inflation rate (e.g. 2.5%)
  monthlyIncomeGoal: number // income goal in today's dollars (e.g. $4,000)
): RetirementPlannerResult {
  const yearsToRetire = Math.max(0, retirementAge - currentAge);
  const yearsInRetirement = Math.max(0, lifeExpectancy - retirementAge);
  
  // 1. Calculate inflation-adjusted monthly income goal at retirement age
  // FV = PV * (1 + inflation)^years
  const inflationAdjustedGoal = monthlyIncomeGoal * Math.pow(1 + inflationRate / 100, yearsToRetire);
  
  // 2. Calculate the required corpus at retirement age to sustain post-retirement years
  // Using growing annuity model: pv = PMT * (1 - (1+i)^-n) / i
  // Real interest rate post-retirement = (1 + nominal) / (1 + inflation) - 1
  const nominalPostRate = annualReturnPost / 100;
  const inflationRateDec = Math.max(-0.99, inflationRate / 100);
  const realPostRate = (1 + nominalPostRate) / (1 + inflationRateDec) - 1;
  
  let corpusNeeded = 0;
  if (realPostRate > 0) {
    const monthlyRealRate = realPostRate / 12;
    const totalMonths = yearsInRetirement * 12;
    const factor = (1 - Math.pow(1 + monthlyRealRate, -totalMonths)) / monthlyRealRate;
    corpusNeeded = inflationAdjustedGoal * factor;
  } else {
    corpusNeeded = inflationAdjustedGoal * yearsInRetirement * 12;
  }

  // 3. Project savings growth until retirement
  let balance = currentSavings;
  let totalContributions = currentSavings;
  const projection: RetirementPlannerResult["projection"] = [];

  for (let year = 1; year <= yearsToRetire; year++) {
    const startBalance = balance;
    let yearInterest = 0;
    
    // Monthly compounding pre-retirement
    for (let m = 0; m < 12; m++) {
      balance += monthlySavings;
      totalContributions += monthlySavings;
      
      const interest = balance * (annualReturnPre / 100 / 12);
      balance += interest;
      yearInterest += interest;
    }

    projection.push({
      age: currentAge + year,
      savings: roundToDecimal(balance, 2),
      contributions: roundToDecimal(totalContributions, 2),
      interest: roundToDecimal(balance - totalContributions, 2),
    });
  }

  const corpusAccumulated = balance;
  const savingsShortfall = Math.max(0, corpusNeeded - corpusAccumulated);

  return {
    corpusAccumulated: roundToDecimal(corpusAccumulated, 2),
    corpusNeeded: roundToDecimal(corpusNeeded, 2),
    savingsShortfall: roundToDecimal(savingsShortfall, 2),
    yearsToRetire,
    projection,
  };
}

/**
 * FIRE (Financial Independence, Retire Early) Calculator
 */
export function fireCalculator(
  annualExpenses: number,
  currentSavings: number,
  savingsRate: number, // as percentage (e.g. 40%)
  investmentReturn: number, // pre-retirement nominal return (e.g. 9%)
  inflationRate: number,
  annualIncome: number
): { yearsToFire: number; targetCorpus: number; endPortfolioValue: number; fireLog: any[] } {
  // 4% Safe Withdrawal Rate implies target corpus = 25 * annual expenses
  let targetCorpus = annualExpenses * 25;
  const nominalRate = investmentReturn / 100;
  const inflationDec = inflationRate / 100;
  
  let balance = currentSavings;
  let year = 0;
  const fireLog: any[] = [];
  
  const annualSavingsAmount = annualIncome * (savingsRate / 100);

  while (balance < targetCorpus && year < 50) {
    year++;
    
    // Compound savings for 1 year
    const interest = balance * nominalRate;
    balance = balance + interest + annualSavingsAmount;
    
    // Expenses and target corpus grow with inflation
    targetCorpus = targetCorpus * (1 + inflationDec);
    
    fireLog.push({
      year,
      portfolioValue: roundToDecimal(balance, 2),
      targetCorpus: roundToDecimal(targetCorpus, 2),
      isFireReached: balance >= targetCorpus,
    });

    if (balance >= targetCorpus) break;
  }

  return {
    yearsToFire: balance >= targetCorpus ? year : -1,
    targetCorpus: roundToDecimal(targetCorpus, 2),
    endPortfolioValue: roundToDecimal(balance, 2),
    fireLog,
  };
}

/**
 * Calculates target retirement corpus needed based on Safe Withdrawal Rate (SWR)
 */
export function corpusNeeded(
  monthlyExpense: number,
  yearsInRetirement: number,
  returnRate: number,
  inflationRate: number
): { corpus: number } {
  const nominalPostRate = returnRate / 100;
  const inflationDec = Math.max(-0.99, inflationRate / 100);
  const realPostRate = (1 + nominalPostRate) / (1 + inflationDec) - 1;
  
  let corpusValue: number;
  if (realPostRate > 0) {
    const monthlyRealRate = realPostRate / 12;
    const totalMonths = yearsInRetirement * 12;
    const factor = (1 - Math.pow(1 + monthlyRealRate, -totalMonths)) / monthlyRealRate;
    corpusValue = roundToDecimal(monthlyExpense * factor, 2);
  } else {
    corpusValue = roundToDecimal(monthlyExpense * yearsInRetirement * 12, 2);
  }
  
  return { corpus: corpusValue };
}

/**
 * Calculates safe withdrawal amount and updates with inflation over retirement years
 */
export function safeWithdrawalRate(
  corpus: number,
  years: number,
  inflationRate: number,
  withdrawalRatePercent = 4,
  returnRate = 5
): { initialAnnualWithdrawal: number; schedule: Array<{ year: number; withdrawal: number; remainingCorpus: number }> } {
  const initialAnnualWithdrawal = corpus * (withdrawalRatePercent / 100);
  const inflationDec = inflationRate / 100;
  const growthRate = returnRate / 100;
  
  let balance = corpus;
  let currentWithdrawal = initialAnnualWithdrawal;
  const schedule: any[] = [];

  for (let year = 1; year <= years; year++) {
    const interest = balance * growthRate;
    balance = Math.max(0, balance + interest - currentWithdrawal);
    
    schedule.push({
      year,
      withdrawal: roundToDecimal(currentWithdrawal, 2),
      remainingCorpus: roundToDecimal(balance, 2),
    });

    currentWithdrawal *= (1 + inflationDec);
  }

  return {
    initialAnnualWithdrawal: roundToDecimal(initialAnnualWithdrawal, 2),
    schedule,
  };
}

/**
 * Estimates pensions for different country rules
 */
export function pensionEstimator(
  yearsWorked: number,
  finalSalary: number,
  countryCode: string
): { annualPension: number } {
  const code = countryCode.toLowerCase();
  const rules = getCountryRules(countryCode);
  const statePension = rules.retirement.statePension;
  
  let pensionValue: number;
  
  if (code === "in") {
    // India EPS calculation: (Pensionable Salary * Service Years) / 70
    const cap = rules.tax.socialSecurity?.cap ?? 15000;
    const denominator = rules.retirement.statePension?.denominator || 70;
    const serviceYearsCap = rules.retirement.statePension?.serviceYearsCap ?? 35;
    pensionValue = roundToDecimal((Math.min(finalSalary, cap) * Math.min(yearsWorked, serviceYearsCap)) / denominator * 12, 2);
  } else if (statePension) {
    const fullAmount = statePension.fullAmount;
    const yearsRequired = statePension.yearsRequired || 35;
    pensionValue = roundToDecimal(yearsRequired > 0 ? (Math.min(yearsWorked, yearsRequired) / yearsRequired) * fullAmount : 0, 2);
  } else {
    // Generic pension: 1.5% of salary per year of service
    const genericRate = rules.retirement.statePension?.genericRate ?? 0.015;
    pensionValue = roundToDecimal(finalSalary * genericRate * yearsWorked, 2);
  }
  
  return { annualPension: pensionValue };
}

export function calculateBudget2026CpfTopUp(
  birthYear: number = 1980,
  cpfRetirementSavings: number = 50000,
  propertyAnnualValue: number = 15000,
  numberOfProperties: number = 1
) {
  const BRS_2026 = 110200;
  const ageIn2026 = 2026 - birthYear;
  const isAgeEligible = birthYear <= 1976;
  const isSavingsEligible = cpfRetirementSavings < BRS_2026;
  const isPropertyEligible = numberOfProperties <= 1;
  const isAVEligible = propertyAnnualValue <= 31000;

  let topUpAmount = 0;

  if (isAgeEligible && isSavingsEligible && isPropertyEligible && isAVEligible) {
    if (propertyAnnualValue <= 21000) {
      topUpAmount = cpfRetirementSavings < 60000 ? 1500 : 1000;
    } else {
      topUpAmount = 500;
    }
  }

  return {
    ageIn2026,
    birthYear,
    cpfRetirementSavings: Math.round(cpfRetirementSavings),
    propertyAnnualValue: Math.round(propertyAnnualValue),
    numberOfProperties,
    topUpAmount,
    isEligible: topUpAmount > 0,
    eligibilityCriteria: {
      ageEligible: isAgeEligible,
      ageEligibleNote: isAgeEligible ? "Born in 1976 or earlier (aged 50+ in 2026)" : "Must be born in 1976 or earlier (aged 50+ in 2026)",
      savingsEligible: isSavingsEligible,
      savingsEligibleNote: isSavingsEligible
        ? `CPF retirement savings of $${cpfRetirementSavings.toLocaleString()} is below the 2026 BRS of $110,200`
        : `CPF retirement savings of $${cpfRetirementSavings.toLocaleString()} meets or exceeds the 2026 BRS of $110,200`,
      propertyEligible: isPropertyEligible,
      propertyEligibleNote: isPropertyEligible
        ? `Owns ${numberOfProperties} property (≤ 1 required)`
        : `Owns ${numberOfProperties} properties (must own ≤ 1)`,
      avEligible: isAVEligible,
      avEligibleNote: isAVEligible
        ? `Property Annual Value of $${propertyAnnualValue.toLocaleString()} is within the $31,000 limit`
        : `Property Annual Value of $${propertyAnnualValue.toLocaleString()} exceeds the $31,000 limit`,
    },
    tierBreakdown: {
      savingsBand: cpfRetirementSavings < 60000 ? "Less than $60,000" : "$60,000 to less than $110,200",
      avBand: propertyAnnualValue <= 21000 ? "Annual Value not more than $21,000" : "$21,001 to $31,000",
      creditTo: "Retirement Account (RA) or Special Account (SA) if RA not yet created",
      creditedIn: "December 2026",
      autoApplied: true,
      noApplicationNeeded: true,
      notificationMethod: "govbenefits website via Singpass in December 2026"
    },
    brs2026: BRS_2026
  };
}

/**
 * Switzerland 13th AHV Pension Payout Estimator (December 2026 Launch)
 * Official Sourcing: Swiss Federal Office of Public Health (BSV / AHV-IV),
 * December 2026 launch. Scale 44 max single: CHF 2,520/mo, married couple cap: CHF 3,780/mo (150% cap).
 * 13th payment equals exactly one extra month (1/12th of annual AHV old-age pension).
 */
export function calculate13thAhvPension(
  maritalStatus: "single" | "married" = "single",
  contributionYears: number = 44,
  monthlyAhvPension: number = 2520,
  averageAnnualSalary: number = 85000
) {
  const years = Math.min(44, Math.max(1, contributionYears || 44));
  const scaleRatio = years / 44;

  // Statutory maximum monthly caps (2026):
  // Single individual max: CHF 2,520/mo (Scale 44)
  // Married couple combined max (150% Plafonierung): CHF 3,780/mo (Scale 44)
  const maxSingleMonthly = 2520 * scaleRatio;
  const maxCoupleMonthly = 3780 * scaleRatio;

  let effectiveMonthlyPension = Math.max(0, monthlyAhvPension || 0);
  let capNote = "Standard Single Pension Rate";

  if (maritalStatus === "married") {
    if (effectiveMonthlyPension > maxCoupleMonthly) {
      effectiveMonthlyPension = maxCoupleMonthly;
      capNote = `Capped at Married Couple 150% Plafonierung (max CHF ${Math.round(maxCoupleMonthly)}/mo for ${years} years)`;
    } else {
      capNote = `Married Couple Rate (under CHF ${Math.round(maxCoupleMonthly)} cap)`;
    }
  } else {
    if (effectiveMonthlyPension > maxSingleMonthly) {
      effectiveMonthlyPension = maxSingleMonthly;
      capNote = `Capped at Individual Scale 44 Maximum (CHF ${Math.round(maxSingleMonthly)}/mo for ${years} years)`;
    }
  }

  const regularAnnualPension = effectiveMonthlyPension * 12;
  const december13thBonus = effectiveMonthlyPension; // 1 full extra month in December
  const totalAnnualAHV = effectiveMonthlyPension * 13;

  const scalePercentage = Number((scaleRatio * 100).toFixed(1));

  return {
    maritalStatus,
    contributionYears: years,
    scalePercentage,
    monthlyAhvPension: Math.round(effectiveMonthlyPension),
    regularAnnualPension: Math.round(regularAnnualPension),
    december13thBonus: Math.round(december13thBonus),
    totalAnnualAHV: Math.round(totalAnnualAHV),
    coupleCapStatus: capNote
  };
}

/**
 * US 401(k) Contribution Limits Maximizer (2026)
 * Official Sourcing: IRS.gov 2026 401(k) Limits Announcement.
 * Base Employee Deferral: $24,500.
 * Standard Catch-Up (Ages 50–59 & 64+): +$8,000 (Total employee max: $32,500).
 * SECURE 2.0 Super Catch-Up (Ages 60–63): +$11,250 (Total employee max: $35,750).
 * Overall 415(c) Total Limit (Employee + Employer): $72,000.
 */
export function calculate401kContributionMaximizer(
  annualSalary: number = 120000,
  age: number = 45,
  paycheckFrequency: "biweekly" | "monthly" | "semi_monthly" | "weekly" = "biweekly",
  ytdContributions: number = 0,
  remainingPaychecks: number = 12,
  employerMatchPercent: number = 50,
  employerMatchCapPercent: number = 6,
  marginalTaxRate: number = 24,
  priorYearW2Income: number = 120000
) {
  const baseLimit = 24500;
  let catchUpLimit = 0;
  let tierName = "Standard Under-50 Employee Deferral Limit ($24,500)";

  if (age >= 60 && age <= 63) {
    catchUpLimit = 11250;
    tierName = "SECURE 2.0 Super Catch-Up Limit ($24,500 + $11,250 = $35,750)";
  } else if (age >= 50) {
    catchUpLimit = 8000;
    tierName = "Standard Age 50+ Catch-Up Limit ($24,500 + $8,000 = $32,500)";
  }

  // IRS Notice 2025-67 indexed SECURE 2.0 Roth catch-up high-earner threshold to $150,000
  const rothThreshold = 150000;
  const isHighEarnerRothMandate = (priorYearW2Income || annualSalary) > rothThreshold && age >= 50;
  const rothMandateAlert = isHighEarnerRothMandate
    ? "⚠️ SECURE 2.0 Roth Mandate: Prior-year W-2 exceeds $150,000 (IRS Notice 2025-67 threshold). All catch-up contributions must be made on a Roth (after-tax) basis."
    : "Pre-Tax or Roth Catch-Up Allowed (Prior-year W-2 ≤ $150,000 threshold).";

  const totalEmployeeLimit = baseLimit + catchUpLimit;
  const remainingEmployeeLimit = Math.max(0, totalEmployeeLimit - (ytdContributions || 0));

  let paychecksPerYear = 26;
  if (paycheckFrequency === "monthly") paychecksPerYear = 12;
  if (paycheckFrequency === "semi_monthly") paychecksPerYear = 24;
  if (paycheckFrequency === "weekly") paychecksPerYear = 52;

  const validRemainingPaychecks = Math.min(paychecksPerYear, Math.max(1, remainingPaychecks || paychecksPerYear));
  const pastPaychecks = Math.max(0, paychecksPerYear - validRemainingPaychecks);

  const perPaycheckAmount = remainingEmployeeLimit / validRemainingPaychecks;
  const paycheckSalary = annualSalary / paychecksPerYear;
  const perPaycheckPercent = paycheckSalary > 0 ? (perPaycheckAmount / paycheckSalary) * 100 : 0;

  // Per-paycheck Employer Match Math (e.g. 50% match up to 6% of salary per paycheck)
  const matchCapPerPaycheck = paycheckSalary * ((employerMatchCapPercent || 0) / 100);
  
  // Match captured on remaining paychecks
  const matchPerRemainingPaycheck = Math.min(perPaycheckAmount, matchCapPerPaycheck) * ((employerMatchPercent || 0) / 100);
  const remainingMatchCaptured = matchPerRemainingPaycheck * validRemainingPaychecks;

  // Match captured on past paychecks (if YTD contributions were made)
  const pastContributionPerPaycheck = pastPaychecks > 0 ? (ytdContributions || 0) / pastPaychecks : 0;
  const matchPerPastPaycheck = Math.min(pastContributionPerPaycheck, matchCapPerPaycheck) * ((employerMatchPercent || 0) / 100);
  const pastMatchCaptured = matchPerPastPaycheck * pastPaychecks;

  const employerMatchDollars = Math.round(remainingMatchCaptured + pastMatchCaptured);

  const totalCombinedContributions = Math.min(72000, totalEmployeeLimit + employerMatchDollars);
  const estimatedTaxSavings = remainingEmployeeLimit * ((marginalTaxRate || 24) / 100);

  return {
    baseLimit,
    catchUpLimit,
    totalEmployeeLimit,
    remainingEmployeeLimit: Math.round(remainingEmployeeLimit),
    perPaycheckAmount: Number(perPaycheckAmount.toFixed(2)),
    perPaycheckPercent: Number(perPaycheckPercent.toFixed(2)),
    employerMatchDollars,
    totalCombinedContributions: Math.round(totalCombinedContributions),
    estimatedTaxSavings: Math.round(estimatedTaxSavings),
    tierName,
    isHighEarnerRothMandate,
    rothMandateAlert
  };
}

/**
 * Singapore CPF Statutory Retirement & Re-Employment Age Transition Simulator (2026)
 * Sourced from MOM.gov.sg and CPF Board Q3 2026 releases (Effective 1 July 2026).
 * Statutory Retirement Age: 63 -> 64.
 * Statutory Re-employment Age: 68 -> 69.
 * SMRA Base Interest: 4.0% p.a.
 * Extra 1% interest on first $60k combined (max $20k OA) + extra 1% on first $30k for age 55+.
 */
export function calculateCpfRetirementAgeTransition(
  currentAge: number = 60,
  raBalance: number = 200000,
  oaBalance: number = 50000,
  targetPayoutAge: number = 65
) {
  const baseSmraRate = 0.04; // 4.0% p.a.
  const baseOaRate = 0.025; // 2.5% p.a.

  // Statutory CPF Board Extra Interest for Age 55+:
  // Applied to first S$60,000 of COMBINED balances (RA first, OA capped at max S$20,000)
  const cappedOa = Math.min(20000, Math.max(0, oaBalance || 0));
  const combinedBalance = Math.max(0, raBalance || 0) + cappedOa;

  let extraInterestTier1 = 0; // +2% on first S$30k combined balance for 55+
  let extraInterestTier2 = 0; // +1% on next S$30k combined balance (S$30k-S$60k)

  if ((currentAge || 60) >= 55) {
    const first30kCombined = Math.min(30000, combinedBalance);
    const next30kCombined = Math.max(0, Math.min(60000, combinedBalance) - 30000);
    extraInterestTier1 = first30kCombined * 0.02;
    extraInterestTier2 = next30kCombined * 0.01;
  } else {
    // Under 55: +1% on first S$60k combined (max S$20k OA)
    const combinedFirst60k = Math.min(60000, combinedBalance);
    extraInterestTier1 = combinedFirst60k * 0.01;
  }

  const annualBaseInterestRA = (raBalance || 0) * baseSmraRate;
  const annualBaseInterestOA = (oaBalance || 0) * baseOaRate;
  const extraInterestBonus = Math.round(extraInterestTier1 + extraInterestTier2);
  const totalAnnualInterestEarned = Math.round(annualBaseInterestRA + annualBaseInterestOA + extraInterestBonus);

  // 1-Year Delayed Drawdown Compounding Bonus (Age 63 -> 64 shift)
  const oneYearCompoundingBonus = Math.round(totalAnnualInterestEarned);
  const projectedRaBalanceIn1Year = Math.round((raBalance || 0) + annualBaseInterestRA + extraInterestBonus);

  // Approximate CPF LIFE monthly payout enhancement (~7% increase in monthly payout per year deferred between 65 & 70)
  const deferredYears = Math.max(0, (targetPayoutAge || 65) - 65);
  const estimatedMonthlyPayoutBoostPercent = Number((deferredYears * 7.0).toFixed(1));

  // Statutory Threshold Rule Note
  const statutoryStatus = "Official July 1, 2026 Shift: Retirement Age 64 | Re-employment Age 69";

  return {
    currentAge: currentAge || 60,
    statutoryRetirementAge: 64,
    statutoryReemploymentAge: 69,
    annualBaseInterestRA: Math.round(annualBaseInterestRA),
    extraInterestBonus: Math.round(extraInterestTier1 + extraInterestTier2),
    totalAnnualInterestEarned,
    oneYearCompoundingBonus,
    projectedRaBalanceIn1Year,
    estimatedMonthlyPayoutBoostPercent,
    statutoryStatus
  };
}

/**
 * Germany Altersvorsorgedepot (Riester Replacement, 2027) Calculator
 * Enacted via Altersvorsorgereformgesetz (Passed May 2026, Launching 1 Jan 2027).
 * Replaces Riester-Rente for new contracts with zero mandatory capital guarantee.
 * Grundzulage (Base Subsidy): 50% on first €360 = €180 + 25% on €360.01–€1,800/yr = €360. Total max base subsidy = €540/yr.
 * Kinderzulage (Child Subsidy): €300 per child/yr.
 * Young Saver Bonus (<25 yrs): €200 one-off top-up.
 */
export function calculateAltersvorsorgedepotGrowth(
  annualContribution: number = 1800,
  numberOfChildren: number = 1,
  isUnder25: "no" | "yes" = "no",
  investmentTermYears: number = 30,
  expectedEtfReturn: number = 7.0
) {
  const contrib = Math.max(0, annualContribution || 1800);

  // Grundförderung (Base State Subsidy):
  // 50% on first €360 contributed (€180)
  // 25% on portion above €360 up to €1,800 (€360)
  const tier1Contrib = Math.min(360, contrib);
  const tier2Contrib = Math.min(1440, Math.max(0, contrib - 360));

  const baseSubsidyTier1 = tier1Contrib * 0.50;
  const baseSubsidyTier2 = tier2Contrib * 0.25;
  const totalBaseSubsidy = Math.round(baseSubsidyTier1 + baseSubsidyTier2);

  // Kinderzulage: €300 per child/yr
  const totalChildSubsidy = Math.max(0, numberOfChildren || 0) * 300;

  // Young Saver Bonus
  const youngBonus = isUnder25 === "yes" ? 200 : 0;

  const totalAnnualStateSubsidy = totalBaseSubsidy + totalChildSubsidy;
  const netPersonalOutOfPocket = contrib; // Out-of-pocket contribution paid by investor from bank account
  const totalAnnualDeposit = contrib + totalAnnualStateSubsidy; // Combined annual deposit into broker depot

  // Compounded ETF Portfolio Growth
  let totalPortfolioValue = youngBonus;
  const r = (expectedEtfReturn || 7.0) / 100;
  const years = Math.max(1, Math.min(50, investmentTermYears || 30));

  for (let year = 1; year <= years; year++) {
    totalPortfolioValue = (totalPortfolioValue + totalAnnualDeposit) * (1 + r);
  }

  const totalPersonalInvested = contrib * years;
  const totalStateSubsidiesReceived = totalAnnualStateSubsidy * years + youngBonus;
  const totalCompoundInterestEarned = Math.max(0, Math.round(totalPortfolioValue) - (totalPersonalInvested + totalStateSubsidiesReceived));

  return {
    annualContribution: Math.round(contrib),
    totalBaseSubsidy,
    annualBaseSubsidy: totalBaseSubsidy,
    totalChildSubsidy,
    annualChildSubsidy: totalChildSubsidy,
    youngBonus,
    totalAnnualStateSubsidy,
    totalAnnualSubsidy: totalAnnualStateSubsidy,
    netPersonalOutOfPocket: Math.round(netPersonalOutOfPocket),
    totalAnnualDeposit: Math.round(totalAnnualDeposit),
    years,
    totalPortfolioValue: Math.round(totalPortfolioValue),
    projectedPortfolioValue: Math.round(totalPortfolioValue),
    totalPersonalInvested: Math.round(totalPersonalInvested),
    totalInvestorContributions: Math.round(totalPersonalInvested),
    totalStateSubsidiesReceived: Math.round(totalStateSubsidiesReceived),
    totalStateSubsidies: Math.round(totalStateSubsidiesReceived),
    totalCompoundInterestEarned: Math.round(totalCompoundInterestEarned),
    effectiveSubsidyRate: contrib > 0 ? Number(((totalAnnualStateSubsidy / contrib) * 100).toFixed(1)) : 0,
    reformNotice: "Enacted via Altersvorsorgereformgesetz (Passed May 2026, Effective 1 Jan 2027)"
  };
}

/**
 * Australian Pension Supplement portability for departures from 20 Sep 2026.
 * The 12-week temporary-travel rule and permanent-move cessation are confirmed
 * in DVA's 24 Sep 2026 news and Services Australia's current travel guidance.
 * DSS Social Security Guide 5.1.9.10, reviewed 21 Sep 2026, calculates the
 * daily supplement from annual rate / 364, equivalent to fortnightly / 14.
 * Main-payment portability can end earlier; caller supplies that duration.
 * Sources:
 * https://www.dva.gov.au/news/latest-stories/changes-to-the-pension-supplement-for-overseas-travel
 * https://www.servicesaustralia.gov.au/travel-outside-australia-rules-for-pension-supplement
 * https://guides.dss.gov.au/social-security-guide/5/1/9/10
 */
export function calculateAuOverseasPensionSupplement(
  fortnightlySupplement: number = 56,
  daysOverseas: number = 98,
  mainPaymentPayableDays: number = 98,
  travelType: "temporary" | "permanent" = "temporary"
) {
  const amount = Number.isFinite(fortnightlySupplement) ? Math.max(0, Math.min(200, fortnightlySupplement)) : 0;
  const days = Number.isFinite(daysOverseas) ? Math.max(0, Math.min(365, Math.floor(daysOverseas))) : 0;
  const mainDays = Number.isFinite(mainPaymentPayableDays) ? Math.max(0, Math.min(365, Math.floor(mainPaymentPayableDays))) : 0;
  const daily = amount / 14;
  const payableDays = travelType === "permanent" ? 0 : Math.min(days, 84, mainDays);
  const unpaidDays = Math.max(0, days - payableDays);
  const roundCents = (value: number) => Math.round(value * 100) / 100;

  const travelNotice = travelType === "permanent"
    ? "A permanent move overseas stops Pension Supplement from departure under the rule effective 20 September 2026. Confirm how your main payment changes separately."
    : mainDays < Math.min(days, 84)
      ? "The main payment limit you entered cuts this estimate short of 12 weeks. Services Australia or DVA must confirm your payment's own travel rules."
      : days > 84
        ? "The 12-week temporary-travel window covers at most 84 days; Pension Supplement stops after that while you remain overseas."
        : "Your temporary trip is inside the 12-week Pension Supplement window, assuming your main payment remains payable throughout.";

  return {
    payableDays,
    supplementDuringTrip: roundCents(daily * payableDays),
    unpaidDays,
    supplementNotPaid: roundCents(daily * unpaidDays),
    dailySupplement: roundCents(daily),
    travelNotice,
  };
}




